// "From Documents" on a work-order card's Plans (Ryan, Oct 1 2026): pick files that are
// already on the job's Documents tile — the drawings sent up with the bid, a permit, a
// spec — and put them on the card's Plans without downloading and re-uploading. Files
// that are already on the Plans are greyed out.
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../services/apiClient';

const TYPE_LABEL = { drawing: 'Drawings & Plans', permit: 'Permits', spec: 'Specifications', submittal: 'Submittals', rfi: 'RFIs', quote: 'Quotes', vendor_quote: 'Vendor Quotes', invoice: 'Invoices', purchase_order: 'Purchase Orders', change_order: 'Change Orders', usa: 'USA Dig Tickets', rate_sheet: 'Rate Sheets', tm: 'T&M Billing', photo: 'Photos', line_items: 'Line Items', other: 'Other' };
// Drawings first — that is what a Plans tab is for — then the rest in a sensible order.
const ORDER = ['drawing', 'permit', 'spec', 'submittal', 'rfi', 'photo', 'other', 'quote', 'vendor_quote', 'line_items', 'rate_sheet', 'tm', 'change_order', 'purchase_order', 'invoice', 'usa'];

export default function PlansFromDocuments({ projectId, plans = [], onAdded, onClose }) {
  const [docs, setDocs] = useState(null);
  const [chosen, setChosen] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => { apiFetch(`/projects/${projectId}/documents`).then(d => setDocs(Array.isArray(d) ? d : [])).catch(() => setDocs([])); }, [projectId]);
  const onPlans = useMemo(() => new Set(plans.map(p => p.filename)), [plans]);
  const groups = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const by = {};
    for (const d of docs || []) {
      if (String(d.id).startsWith('plan:')) continue;   // those ARE the plans
      if (ql && !`${d.title || ''} ${d.original_name || ''}`.toLowerCase().includes(ql)) continue;
      const t = d.doc_type || 'other';
      (by[t] = by[t] || []).push(d);
    }
    return Object.keys(by).sort((a, b) => (ORDER.indexOf(a) + 1 || 99) - (ORDER.indexOf(b) + 1 || 99)).map(t => [t, by[t]]);
  }, [docs, q]);
  const picked = Object.keys(chosen).filter(k => chosen[k]);
  async function add() {
    if (!picked.length || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await apiFetch(`/projects/${projectId}/plans/from-documents`, { method: 'POST', body: JSON.stringify({ ids: picked }) });
      onAdded && onAdded(r);
      onClose && onClose();
    } catch (e) { setErr(e?.error || e?.message || 'Could not add those.'); }
    setBusy(false);
  }
  const isImg = d => /^image\//.test(d.mime_type || '') || /\.(jpe?g|png|gif|webp)$/i.test(d.original_name || d.filename || '');
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 6600, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f766e' }}>📂 Add to Plans from Documents</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Everything on this job's Documents tile. Tick what the crew should have on the card.</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: '#f3f4f6', border: 'none', width: 32, height: 32, borderRadius: 16, fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: '10px 18px 0' }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search the documents…" style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 11px', fontSize: 13 }} />
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 18px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {docs === null && <div style={{ fontSize: 13, color: '#9ca3af' }}>Reading the Documents tile…</div>}
          {docs && groups.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af' }}>{q ? 'Nothing matches that.' : 'Nothing on the Documents tile yet — send the plans up from the bid, or add a file there first.'}</div>}
          {groups.map(([t, list]) => (
            <div key={t}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{TYPE_LABEL[t] || t}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {list.map(d => {
                  const already = onPlans.has(d.filename);
                  return (
                    <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: already ? '#f9fafb' : '#fff', opacity: already ? 0.6 : 1, cursor: already ? 'default' : 'pointer' }}>
                      <input type="checkbox" disabled={already} checked={!!chosen[d.id]} onChange={e => setChosen(c => ({ ...c, [d.id]: e.target.checked }))} />
                      <span style={{ fontSize: 16 }}>{isImg(d) ? '🖼️' : '📄'}</span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title || d.original_name}</span>
                      {d.where && <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1px 7px', whiteSpace: 'nowrap' }}>{d.where}</span>}
                      {already && <span style={{ fontSize: 10.5, fontWeight: 800, color: '#0f766e', whiteSpace: 'nowrap' }}>on the Plans</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
          {err && <span style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 600, flex: 1 }}>{err}</span>}
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={{ padding: '9px 14px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button onClick={add} disabled={!picked.length || busy} style={{ padding: '9px 16px', borderRadius: 9, border: 'none', background: picked.length && !busy ? '#0f766e' : '#cbd5e1', color: '#fff', fontSize: 13, fontWeight: 800, cursor: picked.length && !busy ? 'pointer' : 'default' }}>
            {busy ? 'Adding…' : `Add ${picked.length || ''} to Plans`.replace('  ', ' ')}
          </button>
        </div>
      </div>
    </div>
  );
}
